import { fetchJson } from '@atlassian/jira-fetch/src/index.tsx';
import {
	createResource,
	useResource,
	type ResourceStoreContext,
	type RouterContext,
	type RouterDataContext,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';
import { getIssueKey } from '@atlassian/jira-router-resources-utils/src/utils/get-issue-key/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { RESOURCE_TYPE_PROJECT_CONTEXT } from '@atlassian/jira-router-resources-project-context/src/constants.tsx';
import { consumeDependency } from '@atlassian/jira-router-resources-utils/src/utils/consume-dependency/index.tsx';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import type { ArticleResponse } from './types.tsx';

const jsmIssueViewSlaPanelResource = createResource({
	type: 'JSM_ISSUE_VIEW_SLA_PANEL_RESOURCE',
	getKey: ({ match, query }: RouterContext) => {
		const issueKey = getIssueKey(match);

		if (!issueKey) {
			if (query && query.selectedIssue) {
				return query.selectedIssue || '';
			}
		}

		return issueKey || '';
	},
	getData: async ({ match, query }: RouterDataContext, { tenantContext }: ResourceStoreContext) => {
		if (!tenantContext.appEditions.serviceDesk) {
			return null;
		}

		let issueKey = getIssueKey(match);

		if (!issueKey && fg('clefairy-14_fix_sla_panel_transition_response')) {
			if (query && query.selectedIssue) {
				issueKey = query.selectedIssue;
			}
		}

		if (!issueKey) {
			return null;
		}

		try {
			let url = `/rest/servicedesk/1/servicedesk/sla/issue/${issueKey}`;

			if (fg('clefairy-14_fix_sla_panel_transition_response')) {
				url = `/rest/servicedesk/1/servicedesk/sla/issue/${issueKey}?operation=jsmIssueViewSlaPanelResource`;
			}

			const response = await fetchJson(url, { method: 'GET' });
			if (!response.errors) {
				return response;
			}
		} catch (error: unknown) {
			fireErrorAnalytics({
				error: error instanceof Error ? error : new Error(String(error)),
				sendToPrivacyUnsafeSplunk: true,
				meta: {
					id: 'jsmIssueViewSlaPanelResource',
					packageName: 'jiraRouterResourcesJsmIssueView',
					teamName: 'jsd-shield',
				},
			});
		}

		return null;
	},
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getJsmIssueViewSlaPanelResource = (): RouteResource<any> =>
	jsmIssueViewSlaPanelResource;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useJsmIssueViewSlaPanelResource = (): UseResourceHookResponse<any> => {
	return useResource(jsmIssueViewSlaPanelResource);
};

// eslint-disable-next-line jira/router/no-daisy-chaining-resources
const jsmIssueViewRelatedArticlesResource = createResource({
	type: 'JSM_ISSUE_VIEW_RELATED_ARTICLES_RESOURCE',
	getKey: ({ match, query }: RouterContext) => {
		const issueKey = getIssueKey(match);

		if (!issueKey) {
			if (query && query.selectedIssue) {
				return query.selectedIssue || '';
			}
		}

		return issueKey || '';
	},
	depends: [RESOURCE_TYPE_PROJECT_CONTEXT],
	getData: async (
		{ match, query, dependencies }: RouterDataContext,
		{ tenantContext }: ResourceStoreContext,
	) => {
		if (!tenantContext.appEditions.serviceDesk) {
			return null;
		}

		let issueKey = getIssueKey(match);

		if (!issueKey && fg('clefairy-14_fix_sla_panel_transition_response')) {
			if (query && query.selectedIssue) {
				issueKey = query.selectedIssue;
			}
		}

		if (!issueKey) {
			return null;
		}

		const consumedDependency = await consumeDependency<ProjectContext>(
			dependencies,
			RESOURCE_TYPE_PROJECT_CONTEXT,
		);

		if (consumedDependency) {
			const { projectType } = consumedDependency;
			if (projectType !== 'service_desk') {
				return null;
			}
		}

		if (__SERVER__) {
			let serverUrl = `/api/issue/${issueKey}/articles`;

			if (fg('clefairy-14_fix_sla_panel_transition_response')) {
				serverUrl = `/api/issue/${issueKey}/articles?operation=jsmIssueViewRelatedArticlesResource`;
			}

			return fetchJson<ArticleResponse>(serverUrl, {
				method: 'GET',
				headers: { 'x-ssr-service-name': 'jsd-apollo', 'atl-cloudid': tenantContext.cloudId },
			});
		}

		let clientUrl = `/gateway/api/jsd-apollo-stargate/sharded/workspace/${tenantContext.activationId}/api/issue/${issueKey}/articles`;

		if (fg('clefairy-14_fix_sla_panel_transition_response')) {
			clientUrl = `/gateway/api/jsd-apollo-stargate/sharded/workspace/${tenantContext.activationId}/api/issue/${issueKey}/articles?operation=jsmIssueViewRelatedArticlesResource`;
		}

		return fetchJson<ArticleResponse>(clientUrl, { method: 'GET' });
	},
});

export const getJsmIssueViewRelatedArticlesResource = (): RouteResource<ArticleResponse | null> =>
	jsmIssueViewRelatedArticlesResource;

export const useJsmIssueViewRelatedArticlesResource =
	(): UseResourceHookResponse<ArticleResponse | null> => {
		return useResource(jsmIssueViewRelatedArticlesResource);
	};
